import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, ACCENT_IMAGES } from "@/data/brand";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (!res.ok) { toast.error(res.error || "Registrazione fallita"); return; }
    if (res.verification_required) {
      setSentTo(res.email || email);
      toast.success("Email di attivazione inviata!");
      return;
    }
    // Legacy auto-login fallback
    toast.success("Account creato! Benvenuto su HostMarket.");
    nav("/dashboard");
  };

  if (sentTo) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-8" data-testid="register-verification-sent">
        <div className="w-full max-w-md text-center">
          <Link to="/"><img src={LOGO_URL} alt="HostMarket" className="h-12 w-auto mx-auto mb-8" /></Link>
          <div className="w-20 h-20 rounded-full border-2 border-[#FFBE00] bg-[#FFBE00]/10 flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-envelope text-3xl text-[#FFBE00]"></i>
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl leading-tight mb-3">
            Controlla la tua <span className="text-[#FFBE00]">email</span>
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-2">
            Abbiamo inviato un link di attivazione a
          </p>
          <p className="font-bold text-white text-lg mb-6 break-all" data-testid="verification-sent-email">{sentTo}</p>
          <div className="hm-card p-5 text-left space-y-3 mb-8">
            <div className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFBE00] text-black font-black text-xs flex items-center justify-center">1</span>
              <span className="text-zinc-300">Apri la nostra email nella tua casella (controlla anche <strong className="text-white">spam/promozioni</strong>).</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFBE00] text-black font-black text-xs flex items-center justify-center">2</span>
              <span className="text-zinc-300">Clicca <strong className="text-white">"Attiva account"</strong> nell'email.</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFBE00] text-black font-black text-xs flex items-center justify-center">3</span>
              <span className="text-zinc-300">Torna qui, <strong className="text-white">accedi</strong> e inizia a usare HostMarket.</span>
            </div>
          </div>
          <Link to="/login" className="inline-block px-6 py-3 bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm rounded-md hover:bg-[#FFBE00] hover:text-black transition-all" data-testid="verification-go-to-login">
            Vai al login →
          </Link>
          <p className="mt-6 text-xs text-zinc-500">
            Non hai ricevuto nulla entro 5 minuti?{" "}
            <button onClick={() => setSentTo("")} className="text-[#FFBE00] hover:underline" data-testid="verification-retry-register">
              Riprova la registrazione
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-64px)]" data-testid="register-page">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <Link to="/"><img src={LOGO_URL} alt="HostMarket" className="h-10 w-auto mb-10" /></Link>
          <h1 className="font-heading font-black text-3xl mb-2">Crea il tuo account</h1>
          <p className="text-sm text-zinc-500 mb-8">Hai già un account? <Link to="/login" className="text-[#E619A8] hover:underline">Accedi</Link></p>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Nome / Azienda</label>
              <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-3 text-sm focus:border-[#E619A8] focus:ring-1 focus:ring-[#E619A8] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email</label>
              <input data-testid="register-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-3 text-sm focus:border-[#E619A8] focus:ring-1 focus:ring-[#E619A8] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Password <span className="text-zinc-600">(min 6 caratteri)</span></label>
              <input data-testid="register-password" required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-3 text-sm focus:border-[#E619A8] focus:ring-1 focus:ring-[#E619A8] outline-none" />
            </div>
            <p className="text-xs text-zinc-500 flex items-start gap-2">
              <i className="fas fa-circle-info text-[#FFBE00] mt-0.5"></i>
              <span>Ti invieremo un'<strong className="text-white">email di attivazione</strong>. Dovrai cliccare il link prima di poter accedere.</span>
            </p>
            <button type="submit" disabled={loading} className="w-full py-3 rounded-md bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#FFBE00] hover:text-black transition-all disabled:opacity-50" data-testid="register-submit">
              {loading ? "Invio email…" : "Crea account & ricevi email"}
            </button>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={ACCENT_IMAGES[1]} alt="" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-l from-black/90 via-black/40 to-black/70"></div>
        <div className="absolute bottom-10 left-10 right-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E619A8] mb-2">· Benvenuto</div>
          <h2 className="font-heading font-black text-4xl leading-tight">Unisciti alla <span className="text-[#FFBE00]">community</span> tech degli host.</h2>
        </div>
      </div>
    </div>
  );
}
